// Body of the pillows ContactFormPage: the UtilityPage layout (side nav +
// content title + right bar) with the rich-text intro and the contact form in
// content__copy. The form is rendered natively in React from the
// reactivated-serialized ContactFormRenderable; the captcha is the standalone
// <ReactivatedReCaptchaField> (excluded from serialization).
import { Immutable } from "immer";
import React from "react";
import { FormErrorList } from "tsi-common-react/src/forms/FormErrorList/index";
import { ReactivatedFormWidget } from "tsi-common-react/src/forms/ReactivatedFormWidget/index";
import {
    ReactivatedReCaptchaField,
    useReCaptchaSubmit,
} from "tsi-common-react/src/forms/ReactivatedReCaptcha/index";
import {
    FORM_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
} from "tsi-common-react/src/forms/classNames";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import { CSRFToken } from "@reactivated/forms";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { SideNav } from "../SideNav";

type IProps = Immutable<templates.ContactFormPageTemplate>;

const ContactForm = (
    props: Pick<IProps, "form" | "recaptcha_action">,
): React.JSX.Element => {
    const { form } = props;
    const onSubmit = useReCaptchaSubmit(props.recaptcha_action);
    return (
        <>
            {form.errors && (
                <div className="contact-form__errors-title">
                    {t`There was an error submitting the form. Scroll down for details.`}
                </div>
            )}
            <form
                action=""
                method="post"
                className={concatClassNames([FORM_CLASS_NAME, "form--contact"])}
                onSubmit={onSubmit}
            >
                <CSRFToken />
                {form.iterator.map((name) => {
                    const field = form.fields[name];
                    const errors = form.errors?.[name];
                    return (
                        <div className={FORM_FIELD_CLASS_NAME} key={name}>
                            <ReactivatedFormWidget widget={field.widget} />
                            <label htmlFor={field.widget.attrs.id}>
                                {field.label}
                            </label>
                            {errors && errors.length > 0 && (
                                <FormErrorList errors={errors} />
                            )}
                        </div>
                    );
                })}
                <ReactivatedReCaptchaField />
                <input type="submit" value={t`Submit`} />
            </form>
        </>
    );
};

export const ContactFormPageBody = (props: IProps): React.JSX.Element => {
    const {
        has_nav,
        side_nav,
        has_right_bar,
        right_bar,
        content_title,
        phone_display,
        phone_href,
        intro_html,
    } = props;
    // ttag's `jt` forbids a conditional/logical expression directly inside the
    // `${...}` interpolation, so compute the optional phone link first.
    const phoneLink = phone_display ? (
        <a key="phone" href={phone_href}>
            {phone_display}
        </a>
    ) : null;
    return (
        <div className="l-capped-width u-margin-bottom--xl u-full-width-title">
            {has_nav && (
                <div className="l-side-nav">
                    <SideNav items={side_nav} />
                </div>
            )}

            <div className="l-side-nav-content">
                <div className="content-title">
                    <h1 className="content-title__headline">{content_title}</h1>
                    <span className="content-title__info">
                        {jt`Talk to Sleep Expert ${phoneLink}`}
                    </span>
                </div>

                <div
                    className={
                        has_right_bar
                            ? "content__copy"
                            : "content__copy--full-width"
                    }
                >
                    <div
                        className="rich-text"
                        dangerouslySetInnerHTML={{ __html: intro_html }}
                    />
                    <ContactForm
                        form={props.form}
                        recaptcha_action={props.recaptcha_action}
                    />
                </div>

                {has_right_bar && (
                    <aside className="content__aside">
                        {right_bar.map((item, i) =>
                            item.block_type === "right_bar_section" ? (
                                <div
                                    key={i}
                                    className={`w-block-${item.block_type} block-${item.block_type}`}
                                >
                                    <h1 className="content__aside__title">
                                        {item.header}
                                    </h1>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: item.body_html,
                                        }}
                                    />
                                </div>
                            ) : (
                                <EditorContent key={i}>
                                    {(marker) => (
                                        <div
                                            className={`w-block-${item.block_type} block-${item.block_type}`}
                                            dangerouslySetInnerHTML={{
                                                __html: item.body_html,
                                            }}
                                            {...marker}
                                        />
                                    )}
                                </EditorContent>
                            ),
                        )}
                    </aside>
                )}
            </div>
        </div>
    );
};
